import { type KeyboardEvent, type MouseEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DayPicker, type Matcher } from 'react-day-picker';
import { useOnClickOutside } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/classNames.ts';
import { parseDateTimeValue } from '@/utils/parseDateTimeValue.ts';
import dayjs from '$/utils/dayjs';

const DATE_FORMAT = 'YYYY-MM-DD';

export type DateTimePickerInputProps = {
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  minDate?: Date;
  maxDate?: Date;
  withTime?: boolean;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  timeLabel?: string;
  hint?: string;
};

function DateTimePickerInput({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  withTime = false,
  error,
  placeholder,
  disabled = false,
  className = '',
  timeLabel,
  hint,
}: DateTimePickerInputProps) {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeInputId = useId();
  const [isOpen, setIsOpen] = useState(false);

  const { date, time } = useMemo(() => parseDateTimeValue(value), [value]);

  const selected = useMemo(() => (date ? dayjs(date, DATE_FORMAT).toDate() : undefined), [date]);

  const disabledMatchers = useMemo<Matcher[]>(() => {
    const matchers: Matcher[] = [];

    if (minDate) matchers.push({ before: minDate });
    if (maxDate) matchers.push({ after: maxDate });

    return matchers;
  }, [minDate, maxDate]);

  useOnClickOutside(wrapperRef, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const handleSelectDay = (day: Date | undefined) => {
    if (!day) {
      onChange(undefined);
      return;
    }

    const nextDate = dayjs(day).format(DATE_FORMAT);

    onChange(withTime && time ? `${nextDate}T${time}` : nextDate);
    setIsOpen(false);
  };

  const handleChangeTime = (nextTime: string) => {
    if (!date) return;

    onChange(nextTime ? `${date}T${nextTime}` : date);
  };

  const handleClear = (event: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    onChange(undefined);
  };

  const summary = () => {
    if (!selected) return placeholder ?? t('tripCreateForm.whatHasBeenBooked.pickDate', 'Choose a date');

    return dayjs(selected).format('ddd D MMM YYYY');
  };

  return (
    <div ref={wrapperRef} className={cn('relative block min-w-0 font-sans', className)}>
      <span className="mb-1.5 block text-sm font-medium text-primary">{label}</span>

      <div className={cn('flex gap-2', withTime && 'flex-col sm:flex-row sm:items-start')}>
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-invalid={Boolean(error)}
          onClick={() => setIsOpen((previous) => !previous)}
          className={cn(
            'flex h-12 w-full items-center gap-2 rounded-[10px] border bg-white px-4 text-left font-sans text-base font-medium text-[#191919] outline-none disabled:cursor-not-allowed disabled:opacity-60',
            error ? 'border-error' : 'border-primary',
            isOpen && 'ring-2 ring-primary/20'
          )}
        >
          <CalendarIcon className="h-5 w-5 shrink-0 text-primary" />
          <span className={cn('grow truncate', !selected && 'font-normal text-[#737373]')}>{summary()}</span>

          {selected ? (
            <span
              role="button"
              tabIndex={0}
              aria-label={t('tripCreateForm.whatHasBeenBooked.clearDate', 'Clear date')}
              className="shrink-0 opacity-50 transition-opacity hover:opacity-100"
              onClick={handleClear}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') handleClear(event);
              }}
            >
              <XMarkIcon className="h-4 w-4" />
            </span>
          ) : null}
        </button>

        {withTime ? (
          <label htmlFor={timeInputId} className="flex shrink-0 items-center gap-3 sm:w-40">
            <span className="shrink-0 text-sm font-medium text-primary sm:sr-only">
              {timeLabel ?? t('tripCreateForm.whatHasBeenBooked.fields.time.label', 'Time')}
            </span>
            <input
              id={timeInputId}
              type="time"
              value={time ?? ''}
              disabled={!date || disabled}
              style={{ colorScheme: 'light' }}
              onChange={(event) => handleChangeTime(event.target.value)}
              className={cn(
                'h-12 w-full min-w-0 rounded-[10px] border bg-white px-3 font-sans text-base font-medium tabular-nums text-[#191919] outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60',
                error ? 'border-error' : 'border-primary'
              )}
            />
          </label>
        ) : null}
      </div>

      {isOpen ? (
        <div
          role="dialog"
          aria-label={label}
          className="absolute left-0 top-[calc(100%+8px)] z-30 w-max max-w-[calc(100vw-2rem)] overflow-x-auto rounded-box border border-base-300 bg-base-100 p-4 shadow-xl"
        >
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={handleSelectDay}
            disabled={disabledMatchers}
            startMonth={minDate}
            endMonth={maxDate}
            defaultMonth={selected ?? minDate}
            showOutsideDays={false}
          />

          {minDate && maxDate ? (
            <p className="mt-3 border-t border-base-300 pt-3 text-center text-sm text-text-secondary">
              {t('tripCreateForm.whatHasBeenBooked.withinTripDates', {
                defaultValue: 'Only dates between {{from}} and {{to}} can be chosen',
                from: dayjs(minDate).format('D MMM'),
                to: dayjs(maxDate).format('D MMM'),
              })}
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <span className="mt-1.5 block text-sm text-error" role="alert">
          {error}
        </span>
      ) : null}
      {!error && hint ? <span className="mt-1.5 block text-sm text-text-secondary">{hint}</span> : null}
    </div>
  );
}

export default DateTimePickerInput;
